import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  ETICHETA_STARE_TASK,
  departamentulRolului,
  numeDepartament,
  type DepartamentSlug,
  type StareTask,
} from '@b4l/shared'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { arataDublura } from '../stores/dublura'
import { toast } from 'sonner'
import { api } from '../lib/api'
import {
  AlegeDepartamente,
  listaDepartamente,
  trimiteLa,
} from '../components/taskuri/AlegeDepartamente'
import {
  BlocResponsabili,
  ComutatorDestinatie,
  useResponsabili,
  type ModDestinatie,
} from './taskuri/DestinatieTask'
import { downloadFile } from '../lib/downloadFile'
import { useAuthStore } from '../stores/auth'
import { Card } from '../components/ui/Card'
import { Field } from '../components/ui/Field'
import { Button } from '../components/ui/Button'
import { Modal } from '../components/ui/Modal'
import { Pagination } from '../components/ui/Pagination'
import { useClientPagination } from '../hooks/useClientPagination'

type Bucket = 'unread' | 'all' | 'archived' | 'blocked'

interface InboxMessage {
  id: number
  messageIdImap: string
  fromAddr: string
  toAddr: string
  subject: string
  bodyText: string
  bodyHtml: string | null
  receivedAt: string
  read: boolean
  archived: boolean
  blocked: boolean
  matchedB4lId: string | null
  allMatchedIds: string[]
  appliedFilter: { id: number; name: string; action: string } | null
  relatedTask: { id: number; title: string; status: string } | null
  routedTo: string | null
}

interface ListResponse {
  messages: InboxMessage[]
  total: number
  counts: Record<Bucket, number>
  mailboxes: string[]
}

interface StoredAttachment {
  idx: number
  filename: string
  contentType: string | null
  size: number | null
}
interface DetailResponse {
  message: InboxMessage & {
    appliedFilter: {
      id: number
      name: string
      action: string
      actionTarget: string | null
    } | null
    relatedTask: {
      id: number
      title: string
      status: string
      dueDate: string | null
    } | null
    attachments: StoredAttachment[]
  }
  outboundEmail: {
    id: number
    b4lId: string
    type: string
    recipient: string
    subject: string
    sentAt: string | null
    status: string
  } | null
  shopifyCustomer: {
    id: number
    shopifyId: string
    firstName: string | null
    lastName: string | null
    email: string | null
    phone: string | null
    ordersCount: number
    totalSpent: string
    avgOrderValue: string
    segment: string | null
    daysSinceLastOrder: number | null
    lastOrderAt: string | null
    firstOrderAt: string | null
    city: string | null
  } | null
}

const BUCKET_LABELS: Record<Bucket, string> = {
  unread: 'Necitite',
  all: 'Toate',
  archived: 'Arhivate',
  blocked: 'Blocate',
}

export function InboxPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('inbox:write')

  const [bucket, setBucket] = useState<Bucket>('unread')
  const [search, setSearch] = useState('')
  const [mailbox, setMailbox] = useState('')
  const [openId, setOpenId] = useState<number | null>(null)
  /**
   * Ce e bifat pentru acțiunile în masă.
   *
   * Victor: „eventual să am sus butoane cu care pot face arhivarea în masă". Cu 40–60 de
   * mesaje pe zi, arhivarea rând cu rând înseamnă sute de apăsări pe săptămână — motivul
   * pentru care Inbox-ul nu se golea niciodată.
   */
  const [selectate, setSelectate] = useState<Set<number>>(new Set())

  const query = useQuery<ListResponse>({
    queryKey: ['inbox', bucket, search, mailbox],
    queryFn: async () => {
      const res = await api.get<ListResponse>('/inbox', {
        params: { bucket, search: search || undefined, mailbox: mailbox || undefined, limit: 100 },
      })
      return res.data
    },
    refetchInterval: 30_000,
  })

  const messages = useMemo(() => query.data?.messages ?? [], [query.data])

  const arhiveazaMulte = useMutation({
    mutationFn: async (ids: number[]) =>
      (await api.post<{ arhivate: number }>('/inbox/arhiveaza', { ids })).data,
    onSuccess: (r) => {
      toast.success(`${r.arhivate} mesaje arhivate`)
      setSelectate(new Set())
      void queryClient.invalidateQueries({ queryKey: ['inbox'] })
    },
    onError: () => toast.error('Nu am putut arhiva'),
  })

  /**
   * Blochează EXPEDITORII bifați, pe adresă.
   *
   * Victor: „la blocare trebuie să îmi permită să blochez limitat (spre ex să nu îmi blocheze
   * pe domeniu direct, să blochez doar anumite mail-uri, selectiv)". Distincția contează:
   * `@drmax.ro` trimite și comenzi, și notificări automate — blocat pe domeniu, ar dispărea și
   * comenzile. Domeniul rămâne posibil, dar numai scris anume în Setări → Filtre email.
   */
  const blocheazaExpeditori = useMutation({
    mutationFn: async (v: { adrese: string[]; siCeleVechi: boolean }) =>
      (await api.post<{ reguli: number; ignorate?: string[] }>('/inbox/blocheaza-expeditor', v))
        .data,
    onSuccess: (r) => {
      if (r.reguli > 0)
        toast.success(
          `${r.reguli} ${r.reguli === 1 ? 'expeditor blocat' : 'expeditori blocați'}. Emailurile ajung în continuare în căsuța ta, doar în CRM nu mai apar.`,
        )
      // Doar adrese intregi se blocheaza de aici; domeniile, din Setari → Filtre email.
      if (r.ignorate?.length)
        toast.error(
          `Nu am blocat ${r.ignorate.join(', ')}: nu e o adresă de email întreagă. Un domeniu se blochează din Setări → Filtre email.`,
        )
      setSelectate(new Set())
      void queryClient.invalidateQueries({ queryKey: ['inbox'] })
    },
    onError: () => toast.error('Nu am putut bloca'),
  })
  const counts = query.data?.counts ?? { unread: 0, all: 0, archived: 0, blocked: 0 }
  const mailboxes = query.data?.mailboxes ?? []
  const pag = useClientPagination(messages, 10)

  const patchMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number
      data: { read?: boolean; archived?: boolean }
    }) => (await api.patch(`/inbox/${id}`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
    onError: showError,
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Inbox</h1>
        <div className="text-xs text-b4l-text-muted">
          Mesajele se preiau automat din căsuță; lista se actualizează la 30 de secunde.
        </div>
      </div>

      <div className="flex items-center gap-2">
        {(['unread', 'all', 'archived', 'blocked'] as Bucket[]).map((b) => (
          <button
            key={b}
            type="button"
            onClick={() => setBucket(b)}
            className={`rounded px-3 py-1.5 text-sm ${
              bucket === b
                ? 'bg-b4l-green text-white'
                : 'bg-white border border-b4l-border text-b4l-text hover:bg-b4l-bg'
            }`}
          >
            {BUCKET_LABELS[b]} <span className="ml-1 text-xs opacity-80">({counts[b]})</span>
          </button>
        ))}
        {mailboxes.length > 0 && (
          <select
            value={mailbox}
            onChange={(e) => setMailbox(e.target.value)}
            className="ml-auto rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
            title="Filtrează după căsuță"
          >
            <option value="">Toate căsuțele</option>
            {mailboxes.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        )}
        <div className={`w-72 ${mailboxes.length > 0 ? '' : 'ml-auto'}`}>
          <Field
            label=""
            placeholder="Caută după subiect, expeditor sau ID B4L"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/*
        Bara de acțiuni în masă. Apare doar când e ceva bifat — un rând de butoane inactive,
        permanent pe ecran, e zgomot.
      */}
      {canWrite && selectate.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-b4l-blue bg-b4l-blue-light p-3">
          <span className="text-sm font-medium text-b4l-blue-dark">
            {selectate.size} {selectate.size === 1 ? 'mesaj bifat' : 'mesaje bifate'}
          </span>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setSelectate(new Set())}>
              Renunță
            </Button>
            <Button
              variant="secondary"
              onClick={() => arhiveazaMulte.mutate([...selectate])}
              disabled={arhiveazaMulte.isPending}
            >
              {arhiveazaMulte.isPending ? 'Se arhivează…' : 'Arhivează'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                const adrese = [
                  ...new Set(messages.filter((m) => selectate.has(m.id)).map((m) => m.fromAddr)),
                ]
                if (
                  window.confirm(
                    `Blochezi ${adrese.length} ${adrese.length === 1 ? 'expeditor' : 'expeditori'}?\n\n${adrese.join('\n')}\n\nE-mailurile ajung în continuare în căsuța ta, dar în CRM nu mai apar și nu mai devin task-uri. Se blochează adresele exacte, nu domeniile.`,
                  )
                ) {
                  blocheazaExpeditori.mutate({ adrese, siCeleVechi: true })
                }
              }}
              disabled={blocheazaExpeditori.isPending}
            >
              {blocheazaExpeditori.isPending ? 'Se blochează…' : 'Blochează expeditorul'}
            </Button>
          </div>
        </div>
      )}

      <Card>
        {query.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
        ) : messages.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            Niciun mesaj în {BUCKET_LABELS[bucket].toLowerCase()}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                  <th className="w-8 py-2 pr-2">
                    <input
                      type="checkbox"
                      checked={
                        pag.paginated.length > 0 && pag.paginated.every((m) => selectate.has(m.id))
                      }
                      onChange={(e) =>
                        setSelectate(
                          e.target.checked ? new Set(pag.paginated.map((m) => m.id)) : new Set(),
                        )
                      }
                      aria-label="Bifează tot ce se vede"
                    />
                  </th>
                  <th className="py-2 pr-3 font-medium">Primit</th>
                  <th className="py-2 pr-3 font-medium">De la</th>
                  <th className="py-2 pr-3 font-medium">Subiect</th>
                  <th className="py-2 pr-3 font-medium">ID B4L</th>
                  <th className="py-2 pr-3 font-medium">Filtru</th>
                  <th className="py-2 pr-3 font-medium">Task</th>
                  <th className="py-2 pr-3 font-medium">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {pag.paginated.map((m) => (
                  <tr
                    key={m.id}
                    className={`border-b border-b4l-border last:border-0 cursor-pointer hover:bg-b4l-bg/40 ${
                      !m.read ? 'font-medium' : ''
                    } ${selectate.has(m.id) ? 'bg-b4l-blue-light/40' : ''}`}
                    onClick={() => {
                      setOpenId(m.id)
                      if (!m.read) patchMutation.mutate({ id: m.id, data: { read: true } })
                    }}
                  >
                    {/* Bifa nu deschide mesajul: altfel n-ai putea alege fara sa citesti. */}
                    <td className="py-2 pr-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectate.has(m.id)}
                        onChange={(e) =>
                          setSelectate((prev) => {
                            const nou = new Set(prev)
                            if (e.target.checked) nou.add(m.id)
                            else nou.delete(m.id)
                            return nou
                          })
                        }
                        aria-label={`Bifează mesajul de la ${m.fromAddr}`}
                      />
                    </td>
                    <td className="py-2 pr-3 text-xs text-b4l-text-muted whitespace-nowrap">
                      {formatDate(m.receivedAt)}
                    </td>
                    <td className="py-2 pr-3 text-xs max-w-[200px] truncate" title={m.fromAddr}>
                      {m.fromAddr}
                    </td>
                    <td className="py-2 pr-3 max-w-[360px] truncate" title={m.subject}>
                      {!m.read && (
                        <span className="mr-2 inline-block h-2 w-2 rounded-full bg-b4l-green" />
                      )}
                      {m.subject}
                    </td>
                    <td className="py-2 pr-3 font-mono text-[11px] text-b4l-purple-dark">
                      {m.matchedB4lId ?? '—'}
                    </td>
                    <td className="py-2 pr-3 text-xs">
                      {m.appliedFilter ? (
                        <span title={`acțiune: ${m.appliedFilter.action}`}>
                          {m.appliedFilter.name}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-2 pr-3 text-xs">
                      {m.relatedTask ? (
                        <Link
                          to={`/task-uri?view=${m.relatedTask.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-b4l-blue-dark hover:underline"
                          title={`${m.relatedTask.title} · ${ETICHETA_STARE_TASK[m.relatedTask.status as StareTask] ?? m.relatedTask.status}`}
                        >
                          #{m.relatedTask.id}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-2 pr-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1">
                        {canWrite && bucket !== 'archived' && (
                          <Button
                            variant="secondary"
                            onClick={() =>
                              patchMutation.mutate({ id: m.id, data: { archived: true } })
                            }
                            className="!px-2 !py-1 !text-xs"
                          >
                            Arhivează
                          </Button>
                        )}
                        {canWrite && bucket === 'archived' && (
                          <Button
                            variant="secondary"
                            onClick={() =>
                              patchMutation.mutate({ id: m.id, data: { archived: false } })
                            }
                            className="!px-2 !py-1 !text-xs"
                          >
                            Dezarhivează
                          </Button>
                        )}
                        {canWrite && m.read && (
                          <Button
                            variant="ghost"
                            onClick={() =>
                              patchMutation.mutate({ id: m.id, data: { read: false } })
                            }
                            className="!px-2 !py-1 !text-xs"
                          >
                            Marchează necitit
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3">
          <Pagination
            page={pag.page}
            pageSize={pag.pageSize}
            total={pag.total}
            onPageChange={pag.setPage}
            onPageSizeChange={pag.setPageSize}
            itemLabel="mesaje"
          />
        </div>
      </Card>

      {openId != null && (
        <InboxDetailModal messageId={openId} canWrite={canWrite} onClose={() => setOpenId(null)} />
      )}
    </div>
  )
}

// ============================================================================
// Detail modal
// ============================================================================

function InboxDetailModal({
  messageId,
  canWrite,
  onClose,
}: {
  messageId: number
  canWrite: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const detailQuery = useQuery<DetailResponse>({
    queryKey: ['inbox', messageId, 'detail'],
    queryFn: async () => (await api.get<DetailResponse>(`/inbox/${messageId}`)).data,
  })

  /*
    Task-urile sunt pe departamente (12.10.2026): emailul devine task al unui DEPARTAMENT, nu al
    unui om ales. Pentru departamentul tau e deja al tau (l-ai citit si ai hotarat ca e o treaba);
    pentru altul ajunge la „De preluat" acolo, ca cerere de la voi. Adminul n-are departament,
    deci il alege. Termenul e optional — doar cand exista o data limita reala.
  */
  const [converteste, setConverteste] = useState(false)
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const alMeu = user ? departamentulRolului(user.role) : null
  const [alese, setAlese] = useState<DepartamentSlug[]>(alMeu ? [alMeu] : [])
  const [termen, setTermen] = useState('')
  const [urgent, setUrgent] = useState(false)
  // Un singur departament: ca pana acum. Mai multe (doar admin): cate un task la fiecare.
  const departament = alese.length === 1 ? alese[0]! : null
  const maiMulte = alese.length > 1
  const laNoi = !!departament && departament === alMeu
  /*
    Ca la „Task nou" (Madalin, 19.10.2026): emailul merge FIE la departamente, FIE direct la
    unul sau mai multi responsabili — si privat. Victor imparte asa ce vine pe contact@.
  */
  const [mod, setMod] = useState<ModDestinatie>('departamente')
  const resp = useResponsabili(alMeu)
  const peResponsabili = mod === 'responsabili'

  const convertMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ task: { id: number } }>(`/inbox/${messageId}/convert-to-task`, {
          urgent,
          ...(peResponsabili ? resp.corp() : alese.length ? { departamente: alese } : {}),
          ...(termen ? { dueDate: termen } : {}),
        })
      ).data,
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['inbox'] })
      queryClient.invalidateQueries({ queryKey: ['inbox', messageId, 'detail'] })
      queryClient.invalidateQueries({ queryKey: ['taskuri'] })
      toast.success(
        peResponsabili
          ? resp.privat
            ? 'Task privat creat.'
            : 'Task creat, direct „În lucru” la responsabili.'
          : laNoi
            ? 'Task creat și preluat de tine.'
            : `Trimis la ${listaDepartamente(alese)}, la „De preluat”.`,
        { action: { label: 'Deschide', onClick: () => navigate(`/task-uri?view=${r.task.id}`) } },
      )
      setConverteste(false)
    },
    onError: (e) => {
      if (arataDublura(e, data?.message?.bodyText?.slice(0, 2000) ?? '')) return
      showError(e)
    },
  })

  const data = detailQuery.data
  const m = data?.message
  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={m ? m.subject : 'Mesaj inbox'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Închide
          </Button>
          {canWrite && m && !m.relatedTask && !converteste && (
            <Button onClick={() => setConverteste(true)}>Convertește în task</Button>
          )}
          {canWrite && m && !m.relatedTask && converteste && (
            <Button
              disabled={peResponsabili ? !!resp.lipsa : !alese.length}
              onClick={() => convertMutation.mutate()}
              loading={convertMutation.isPending}
            >
              {peResponsabili ? resp.textButon : laNoi ? 'Creează și preiau eu' : trimiteLa(alese)}
            </Button>
          )}
        </>
      }
    >
      {detailQuery.isLoading || !data || !m ? (
        <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
      ) : (
        <div className="space-y-4">
          {converteste && (
            <div className="grid gap-3 rounded-caseta border border-b4l-blue/30 bg-b4l-blue-light/40 p-3">
              <p className="text-corp font-medium text-b4l-text">Task nou din acest email</p>
              <ComutatorDestinatie mod={mod} setMod={setMod} />
              {peResponsabili ? (
                <BlocResponsabili r={resp} alMeu={alMeu} />
              ) : (
                <div className="grid gap-1.5">
                  <span className="text-mic font-semibold text-b4l-text-muted">
                    {user?.role === 'admin' ? 'Pentru ce departamente?' : 'Pentru ce departament?'}
                  </span>
                  <AlegeDepartamente alese={alese} setAlese={setAlese} alMeu={alMeu} />
                  <span className="text-mic text-b4l-text-muted">
                    {alese.length === 0
                      ? 'Departamentul preia task-ul; nu se alege un coleg anume.'
                      : maiMulte
                        ? `Pleacă ${alese.length} task-uri, câte unul la ${listaDepartamente(alese)}, în „De preluat”. Fiecare îl preia și îl închide separat; emailul rămâne legat de toate.`
                        : laNoi
                          ? 'Îl preiei tu, direct în „În lucru” — l-ai citit și ai hotărât că e o treabă.'
                          : alMeu
                            ? `Ajunge la ${numeDepartament(departament ?? '')}, în „De preluat”. Îl vedeți la „Trimise de noi”.`
                            : `Ajunge la ${numeDepartament(departament ?? '')}, în „De preluat”.`}
                  </span>
                </div>
              )}
              <div className="flex flex-wrap items-end gap-3">
                <label className="block">
                  <span className="mb-0.5 block text-mic text-b4l-text-muted">
                    Termen <span className="text-b4l-text-dim">(opțional)</span>
                  </span>
                  <input
                    type="date"
                    className="rounded-caseta border border-b4l-border bg-b4l-surface px-2 py-1.5 text-corp text-b4l-text"
                    value={termen}
                    onChange={(e) => setTermen(e.target.value)}
                  />
                </label>
                <label className="flex items-center gap-2 pb-1.5 text-corp text-b4l-text">
                  <input
                    type="checkbox"
                    checked={urgent}
                    onChange={(e) => setUrgent(e.target.checked)}
                    className="accent-b4l-red"
                  />
                  Urgent
                </label>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-sm">
            <Tag label="De la" value={m.fromAddr} />
            <Tag label="Către" value={m.toAddr} />
            <Tag label="Primit" value={formatDate(m.receivedAt)} />
            <Tag label="ID B4L identificat" value={m.matchedB4lId ?? '—'} mono />
            <Tag
              label="Filtru aplicat"
              value={
                m.appliedFilter
                  ? `${m.appliedFilter.name} → ${m.appliedFilter.action}${
                      m.appliedFilter.actionTarget ? ` (${m.appliedFilter.actionTarget})` : ''
                    }`
                  : '—'
              }
            />
            <Tag
              label="Task asociat"
              value={m.relatedTask ? `#${m.relatedTask.id} - ${m.relatedTask.title}` : '—'}
            />
          </div>

          {m.allMatchedIds.length > 1 && (
            <div className="rounded border border-b4l-border bg-b4l-bg p-2 text-xs">
              <span className="text-b4l-text-muted">Toate ID-urile B4L găsite:</span>{' '}
              <span className="font-mono">{m.allMatchedIds.join(', ')}</span>
            </div>
          )}

          {data.outboundEmail && (
            <div className="rounded border border-b4l-blue-light bg-b4l-blue-light/30 p-3 text-xs">
              <div className="font-medium text-b4l-blue-dark">
                Răspuns la email-ul nostru {data.outboundEmail.b4lId}
              </div>
              <div className="mt-1 text-b4l-text-muted">
                Tip: {data.outboundEmail.type} · Către: {data.outboundEmail.recipient} · Trimis:{' '}
                {data.outboundEmail.sentAt ? formatDate(data.outboundEmail.sentAt) : '—'}
              </div>
              <div className="mt-1">
                Subiect original: <span className="font-medium">{data.outboundEmail.subject}</span>
              </div>
            </div>
          )}

          {data.shopifyCustomer && (
            <div className="rounded border border-b4l-purple-light bg-b4l-purple-light/30 p-3 text-xs">
              <div className="flex items-center justify-between mb-1">
                <div className="font-medium text-b4l-purple-dark">
                  🛒 Client Shopify identificat
                </div>
                {data.shopifyCustomer.segment && (
                  <span className="rounded bg-white/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase">
                    {data.shopifyCustomer.segment}
                  </span>
                )}
              </div>
              <div className="font-medium">
                {data.shopifyCustomer.firstName || data.shopifyCustomer.lastName
                  ? `${data.shopifyCustomer.firstName ?? ''} ${data.shopifyCustomer.lastName ?? ''}`.trim()
                  : data.shopifyCustomer.email}
                {data.shopifyCustomer.phone && (
                  <span className="ml-2 text-b4l-text-muted">· {data.shopifyCustomer.phone}</span>
                )}
              </div>
              <div className="mt-1 grid grid-cols-2 gap-1 md:grid-cols-4">
                <div>
                  <span className="text-b4l-text-muted">Ordere:</span>{' '}
                  <span className="font-semibold">{data.shopifyCustomer.ordersCount}</span>
                </div>
                <div>
                  <span className="text-b4l-text-muted">LTV:</span>{' '}
                  <span className="font-semibold">
                    {Number(data.shopifyCustomer.totalSpent).toFixed(0)} RON
                  </span>
                </div>
                <div>
                  <span className="text-b4l-text-muted">AOV:</span>{' '}
                  <span className="font-semibold">
                    {Number(data.shopifyCustomer.avgOrderValue).toFixed(0)} RON
                  </span>
                </div>
                <div>
                  <span className="text-b4l-text-muted">Inactiv:</span>{' '}
                  <span className="font-semibold">
                    {data.shopifyCustomer.daysSinceLastOrder !== null
                      ? `${data.shopifyCustomer.daysSinceLastOrder}z`
                      : '-'}
                  </span>
                </div>
              </div>
              <div className="mt-1 text-b4l-text-muted">
                {data.shopifyCustomer.city && <>📍 {data.shopifyCustomer.city} · </>}
                Ultima comandă:{' '}
                {data.shopifyCustomer.lastOrderAt
                  ? new Date(data.shopifyCustomer.lastOrderAt).toLocaleDateString('en-GB')
                  : '—'}
              </div>
            </div>
          )}

          <InboxAttachments
            messageId={m.id}
            messageIdImap={m.messageIdImap}
            stored={m.attachments ?? []}
          />

          <div>
            <div className="mb-1 text-xs font-medium text-b4l-text-muted">Conținut</div>
            <pre className="max-h-[400px] overflow-y-auto whitespace-pre-wrap rounded border border-b4l-border bg-b4l-bg p-3 text-xs">
              {m.bodyText || '(corpul mesajului e gol)'}
            </pre>
          </div>
        </div>
      )}
    </Modal>
  )
}

interface EmailAttachment {
  index: number
  filename: string
  contentType?: string
  size?: number
}
interface FetchEmailResponse {
  attachments: EmailAttachment[]
  source: string
}

/**
 * Listează atașamentele. Preferă atașamentele SALVATE la ingestie (mereu
 * disponibile); pentru mesaje vechi fără salvare, cade pe IMAP live.
 */
function InboxAttachments({
  messageId,
  messageIdImap,
  stored,
}: {
  messageId: number
  messageIdImap: string
  stored: StoredAttachment[]
}) {
  const [downloading, setDownloading] = useState<number | null>(null)
  const hasStored = stored.length > 0

  const live = useQuery<FetchEmailResponse>({
    queryKey: ['inbox', 'attachments-live', messageIdImap],
    queryFn: async () =>
      (
        await api.get<FetchEmailResponse>('/inbox/fetch-email', {
          params: { messageId: messageIdImap },
        })
      ).data,
    enabled: !hasStored && !!messageIdImap,
    staleTime: 5 * 60_000,
    retry: false,
  })

  const download = async (idx: number, filename: string) => {
    setDownloading(idx)
    try {
      if (hasStored) {
        await downloadFile(`/inbox/${messageId}/attachment/${idx}`, {}, filename)
      } else {
        await downloadFile(
          '/inbox/fetch-attachment',
          { messageId: messageIdImap, index: idx },
          filename,
        )
      }
    } catch (e) {
      toast.error((e as Error).message || 'Descărcare eșuată')
    } finally {
      setDownloading(null)
    }
  }

  const items = hasStored
    ? stored.map((a) => ({
        idx: a.idx,
        filename: a.filename,
        contentType: a.contentType,
        size: a.size,
      }))
    : (live.data?.attachments ?? []).map((a) => ({
        idx: a.index,
        filename: a.filename,
        contentType: a.contentType ?? null,
        size: a.size ?? null,
      }))

  if (!hasStored && live.isLoading) {
    return <div className="text-xs text-b4l-text-muted">Se verifică atașamentele…</div>
  }
  if (!hasStored && live.isError) {
    return (
      <div className="text-xs text-b4l-amber-dark">
        Nu am putut încărca atașamentele din IMAP (mesaj vechi, posibil mutat din căsuță).
      </div>
    )
  }
  if (items.length === 0) return null

  return (
    <div>
      <div className="mb-1 text-xs font-medium text-b4l-text-muted">
        Atașamente ({items.length})
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((att) => (
          <button
            key={att.idx}
            type="button"
            onClick={() => download(att.idx, att.filename)}
            disabled={downloading === att.idx}
            className="flex items-center gap-2 rounded border border-b4l-border bg-b4l-bg px-2.5 py-1.5 text-xs hover:border-b4l-blue disabled:opacity-50"
            title={att.contentType ?? undefined}
          >
            <span>📎</span>
            <span className="max-w-[220px] truncate font-medium">{att.filename}</span>
            {att.size != null && (
              <span className="text-b4l-text-muted">{formatFileSize(att.size)}</span>
            )}
            {downloading === att.idx && <span className="text-b4l-text-muted">…</span>}
          </button>
        ))}
      </div>
    </div>
  )
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function Tag({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs text-b4l-text-muted">{label}</div>
      <div className={`mt-0.5 ${mono ? 'font-mono text-[11px]' : ''}`}>{value}</div>
    </div>
  )
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function showError(err: unknown) {
  const message =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(message)
}
